import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Save, Sparkles, MessageSquareQuote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Admin — Библиотека шаблонов ответов.
 *
 * Manager-facing library that powers the "📋 Шаблоны" popover in the
 * WhatsApp chat panel. Categories are fixed (server-side enum); titles and
 * text are editable. Text supports {client_name}, {product}, {price},
 * {city}, {manager_name} variables that get resolved at send time.
 */
export default function AdminReplyTemplates() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, category, title, text}
  const [showEditor, setShowEditor] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reply-templates`, { withCredentials: true });
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
      setMe(r.data.me || null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ category: categories[0]?.key || "other", title: "", text: "" });
    setShowEditor(true);
  };
  const openEdit = (t) => {
    setEditing({ id: t.id, category: t.category, title: t.title, text: t.text });
    setShowEditor(true);
  };

  const save = async () => {
    if (!editing.title.trim() || !editing.text.trim()) return;
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/reply-templates/${editing.id}`, {
          category: editing.category, title: editing.title, text: editing.text,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reply-templates`, {
          category: editing.category, title: editing.title, text: editing.text,
        }, { withCredentials: true });
      }
      setShowEditor(false); setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось сохранить");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Удалить шаблон «${t.title}»?`)) return;
    try {
      await axios.delete(`${API}/admin/reply-templates/${t.id}`, { withCredentials: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось удалить");
    }
  };

  const canDelete = (t) => {
    if (!me) return false;
    if (me.is_admin) return true;
    if (t.is_default) return false;
    return (t.created_by_id || "") === me.id;
  };
  const canEdit = (t) => {
    if (!me) return false;
    if (me.is_admin) return true;
    return !t.is_default;
  };

  const shown = filter === "all" ? items : items.filter(t => t.category === filter);

  return (
    <div data-testid="admin-reply-templates" style={{ maxWidth: 960 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <MessageSquareQuote size={22} style={{ color: "#C5A059" }} />
        <h2 style={{ margin: 0, fontFamily: "Manrope", fontSize: 20, fontWeight: 700, color: "#0A0A0A" }}>
          Шаблоны ответов
        </h2>
        <span style={{ color: "#8a8a89", fontFamily: "Manrope", fontSize: 12 }}>
          Быстрые ответы для WhatsApp-чата · {items.length} шт.
        </span>
        <button data-testid="tpl-new-btn" onClick={openNew}
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6,
                   background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                   padding: "8px 14px", borderRadius: 6, border: "none",
                   fontFamily: "Manrope", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
          <Plus size={14} /> Новый шаблон
        </button>
      </div>

      {/* Variables hint */}
      <div style={{ background: "#FDF6E8", border: "1px solid #F3E2B8", borderRadius: 8,
                    padding: "12px 14px", marginBottom: 18, fontFamily: "Manrope", fontSize: 12,
                    color: "#7A5A15", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Sparkles size={14} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <b>Переменные</b> — в тексте шаблона можно использовать:
          {" "}<code>{"{client_name}"}</code>, <code>{"{product}"}</code>, <code>{"{price}"}</code>,
          {" "}<code>{"{city}"}</code>, <code>{"{manager_name}"}</code>.
          {" "}Они автоматически подставятся, когда менеджер выберет шаблон в чате.
          {me && !me.is_admin && (
            <div style={{ marginTop: 6, color: "#8a8a89" }}>
              Вы можете свободно создавать шаблоны. Дефолтные редактирует только администратор,
              а удалять можно только свои шаблоны.
            </div>
          )}
        </div>
      </div>

      {/* Category filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>Все ({items.length})</FilterChip>
        {categories.map(c => {
          const count = items.filter(t => t.category === c.key).length;
          if (count === 0) return null;
          return <FilterChip key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)}>
            {c.label} ({count})
          </FilterChip>;
        })}
      </div>

      {loading && <div style={{ color: "#8a8a89", fontFamily: "Manrope" }}>Загрузка…</div>}

      <div style={{ display: "grid", gap: 8 }}>
        {shown.map(t => (
          <div key={t.id} data-testid={`tpl-item-${t.id}`}
            style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8,
                     padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                                color: "#C5A059", fontWeight: 700, fontFamily: "Manrope" }}>
                  {t.category_label}
                </span>
                <span style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 14, color: "#0A0A0A" }}>
                  {t.title}
                </span>
                {t.is_default && <span style={{ fontSize: 9, color: "#8a8a89",
                  background: "#f5f5f4", padding: "2px 6px", borderRadius: 3 }}>По умолчанию</span>}
                {!t.is_default && t.created_by_name && (
                  <span style={{ fontSize: 10, color: "#8a8a89", fontFamily: "Manrope" }}>
                    · автор: {t.created_by_name}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#3f3f46",
                            whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {t.text}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {canEdit(t) && (
                <IconBtn onClick={() => openEdit(t)} title="Изменить" data-testid={`tpl-edit-${t.id}`}>
                  <Edit2 size={14} />
                </IconBtn>
              )}
              {canDelete(t) && (
                <IconBtn onClick={() => remove(t)} title="Удалить" data-testid={`tpl-delete-${t.id}`}>
                  <Trash2 size={14} />
                </IconBtn>
              )}
            </div>
          </div>
        ))}
        {!loading && shown.length === 0 && (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#8a8a89", fontFamily: "Manrope" }}>
            Нет шаблонов в этой категории.
          </div>
        )}
      </div>

      {showEditor && editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "white", borderRadius: 10, padding: 24, width: 560, maxWidth: "100%",
                        color: "#0A0A0A" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontFamily: "Manrope", fontWeight: 700, fontSize: 17 }}>
                {editing.id ? "Изменить шаблон" : "Новый шаблон"}
              </h3>
              <button onClick={() => setShowEditor(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#8a8a89" }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Категория">
                <select data-testid="tpl-category" value={editing.category}
                  onChange={e => setEditing({ ...editing, category: e.target.value })}
                  style={inputStyle}>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Название шаблона">
                <input data-testid="tpl-title" value={editing.title}
                  onChange={e => setEditing({ ...editing, title: e.target.value })}
                  placeholder="Например: Приглашение на примерку" style={inputStyle} />
              </Field>
              <Field label={
                <>Текст сообщения <span style={{ color: "#8a8a89", fontWeight: 400 }}>
                  · переменные: {"{client_name}, {product}, {price}, {city}, {manager_name}"}
                </span></>
              }>
                <textarea data-testid="tpl-text" value={editing.text}
                  onChange={e => setEditing({ ...editing, text: e.target.value })}
                  rows={6} placeholder="Здравствуйте, {client_name}! …"
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "Manrope", lineHeight: 1.5 }} />
              </Field>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowEditor(false)}
                style={{ background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
                         padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: 13,
                         color: "#0A0A0A" }}>
                Отмена
              </button>
              <button data-testid="tpl-save-btn" onClick={save}
                disabled={!editing.title.trim() || !editing.text.trim()}
                style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                         border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer",
                         fontFamily: "Manrope", fontWeight: 700, fontSize: 13,
                         display: "flex", alignItems: "center", gap: 6,
                         opacity: (!editing.title.trim() || !editing.text.trim()) ? 0.5 : 1 }}>
                <Save size={14} /> Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 6,
  fontFamily: "Manrope", fontSize: 13, outline: "none", background: "white",
  color: "#0A0A0A",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontFamily: "Manrope", fontSize: 11, fontWeight: 700, color: "#0A0A0A",
                    textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ padding: "5px 12px", borderRadius: 999, border: "1px solid",
               borderColor: active ? "#C5A059" : "#e5e7eb",
               background: active ? "rgba(197,160,89,0.12)" : "white",
               color: active ? "#7A5A15" : "#3f3f46",
               fontFamily: "Manrope", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
      {children}
    </button>
  );
}

function IconBtn({ children, ...props }) {
  return (
    <button {...props}
      style={{ background: "#fafaf9", border: "1px solid #e5e7eb", borderRadius: 6,
               padding: "6px 8px", cursor: "pointer", color: "#3f3f46",
               display: "flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </button>
  );
}
